import React, { forwardRef, useMemo, useEffect, useState } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from "@mui/material";
import {
    PieChart,
    Pie,
    Tooltip,
    Legend,
    Cell,
    ResponsiveContainer
} from "recharts";

// This is the key we used in the button component
const storageKey2 = "inventoryReportData";

/**
 * Determines the stock status of an item.
 */
const getStockStatus = (item) => {
    if (!item.stockmanaged) {
        return 'Not Managed';
    }
    if (item.currentStock <= 0) {
        return 'Out of Stock';
    }
    if (item.currentStock <= item.minThr) {
        return 'Low Stock';
    }
    return 'In Stock';
};

/**
 * Processes the raw inventory data array into a structured report.
 */
const processInventoryReportData = (serverData) => {
    // ALWAYS initialize the full return structure
    const result = {
        kpis: {
            totalCostValue: 0,
            totalRetailValue: 0,
            itemsBelowMin: 0,
            itemCount: 0,
        },
        stockSummary: [],
        detailedReport: [],
    };

    if (!serverData || !Array.isArray(serverData) || serverData.length === 0) {
        return result; // Return the zeroed-out structure
    }

    const stockSummaryMap = {
        'In Stock': 0,
        'Low Stock': 0,
        'Out of Stock': 0,
    };

    serverData.forEach(item => {
        if (item.stockmanaged) {
            const stockStatus = getStockStatus(item);
            const currentStock = item.currentStock || 0;
            const pCost = item.pCost || 0;
            const price = item.price || 0;
            const totalCostValue = pCost * currentStock;
            const totalRetailValue = price * currentStock;

            result.kpis.totalCostValue += totalCostValue;
            result.kpis.totalRetailValue += totalRetailValue;
            result.kpis.itemCount++;
            if (stockStatus === 'Low Stock' || stockStatus === 'Out of Stock') {
                result.kpis.itemsBelowMin++;
            }

            if (stockSummaryMap.hasOwnProperty(stockStatus)) {
                stockSummaryMap[stockStatus]++;
            }

            result.detailedReport.push({
                ...item,
                stockStatus,
                totalCostValue,
                totalRetailValue,
            });
        }
    });

    result.stockSummary = Object.keys(stockSummaryMap)
        .filter(key => stockSummaryMap[key] > 0)
        .map(key => ({
            name: key,
            value: stockSummaryMap[key]
        }));

    return result;
};

// --- Main Component ---
export const InventoryReportPage = forwardRef((props, ref) => {
    const theme = useTheme();
    const [finalData, setFinalData] = useState(null); // Initialize as null
    const [error, setError] = useState(null);

    // This useEffect runs ONCE when the new tab opens
    useEffect(() => {
        console.log("Stock Report page loaded, attempting to read from sessionStorage...");
        const stored = sessionStorage.getItem(storageKey2);
        
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // The data is expected to be in { rawData: [...] }
                setFinalData(parsed.rawData || []); 
                console.log("Data found, processing report...", parsed.rawData);
                
                // Optional: Clear storage after reading
                // sessionStorage.removeItem(storageKey2);

            } catch (err) {
                console.error("Failed to parse stored inventory report data:", err);
                setError("Failed to load report. Invalid data format.");
            }
        } else {
             console.warn("No report data found in sessionStorage.");
             setError("No report data found. Please generate the report again from the main app.");
        }
    }, []); // Empty dependency array, runs once on mount

    // Use useMemo to process the data only when finalData changes.
    const reportData = useMemo(() => processInventoryReportData(finalData), [finalData]);

    const STATUS_COLORS = {
        'In Stock': theme.palette.success.main, // Green
        'Low Stock': theme.palette.warning.main, // Orange
        'Out of Stock': theme.palette.error.main, // Red
    };

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center', backgroundColor: 'error.light' }}>
                    <Typography variant="h6" color="error.dark">
                        {error}
                    </Typography>
                </Paper>
            </Container>
        );
    }
    
    if (!reportData) {
        // Show this while processing or if data is missing
        return (
             <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Loading report data...
                </Typography>
            </Container>
        );
    }

    if (reportData.detailedReport.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No inventory data available for this report.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    // --- If we get here, data is valid ---
    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Inventory Stock Report
            </Typography>
            {/* --- VISUAL SUMMARY & KPIs --- */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>

                {/* 1. KPI Section */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '400px', backgroundColor: '#e3f2fd' }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>Key Inventory Metrics</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" display="block" color="text.secondary">Total Value (Cost)</Typography>
                            <Typography variant="h5" color="primary.dark" sx={{ fontWeight: 'bold' }}>
                                ${reportData.kpis.totalCostValue.toFixed(2)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" display="block" color="text.secondary">Total Value (Retail)</Typography>
                            <Typography variant="h5" color="success.dark" sx={{ fontWeight: 'bold' }}>
                                ${reportData.kpis.totalRetailValue.toFixed(2)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" display="block" color="text.secondary">Items Below Minimum</Typography>
                            <Typography variant="h5" color="error.dark" sx={{ fontWeight: 'bold' }}>
                                {reportData.kpis.itemsBelowMin}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* 2. Pie Chart Section */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '400px', backgroundColor: '#e8f5e9' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Stock Status Summary</Typography>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={reportData.stockSummary}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                label={(entry) => `${entry.name}: ${entry.value}`}
                            >
                                {reportData.stockSummary.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || theme.palette.grey[400]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [`${value} items`, name]} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Paper>
            </Box>

            {/* --- DETAILED INVENTORY LOG TABLE --- */}
            <Paper elevation={3} sx={{ p: 3, minWidth: '450px', backgroundColor: '#fafafa' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Detailed Inventory Report</Typography>
                <TableContainer component={Box}> {/* Removed maxHeight */}
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Current Stock</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Min Threshold</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cost (Unit)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price (Unit)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Value (Cost)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.detailedReport.map((row) => (
                                <TableRow
                                    key={row._id}
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                                        '&:hover': { backgroundColor: theme.palette.action.selected },
                                        // Highlight rows based on status
                                        backgroundColor: row.stockStatus === 'Low Stock' ? '#fff8e1' : row.stockStatus === 'Out of Stock' ? '#ffebee' : 'inherit'
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold' }}>{row.item}</TableCell>
                                    <TableCell>{row.supplier || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{
                                            fontWeight: 'bold',
                                            color: STATUS_COLORS[row.stockStatus] || 'text.primary'
                                        }}>
                                            {row.stockStatus}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{row.currentStock}</TableCell>
                                    <TableCell align="right">{row.minThr}</TableCell>
                                    <TableCell align="right">${(row.pCost || 0).toFixed(2)}</TableCell>
                                    <TableCell align="right">${(row.price || 0).toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        ${row.totalCostValue.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

        </Container>
    );
});